import { isDefined, isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import type { ExpiryDate, GoodTillCancelled } from '@/store/orders/takeProfitOrder/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type SubmitTakeProfitRequest = {
  tileId: string;

  currency1: string;
  currency2: string;
  clientId: number;
  way: ClientWay;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex;
  limitPrice?: number;
  customerPrice?: number;
  margin?: number;
  expiry: GoodTillCancelled | ExpiryDate;
  notificationEmails: string[];
  localTimeWithOffSet: Timestamp;
};

export type SubmitTakeProfitBody = {
  isGtc: boolean;
  bdrId: string; // '20252';
  amountInCcy1?: number;
  amountInCcy2?: number;
  ccyPair: string; // 'EUR/USD';
  way: ClientWay;
  limitPrice: string; // '1';
  base64Emails: string; // base64 encoded emails
  margin: number; // 11.5;
  customerPrice: string; // '0.99885';
  localTimeWithOffSet: Timestamp;
  expiryDay: string | undefined;
  expiryTime: string | undefined;
};

export const submitTakeProfit = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, SubmitTakeProfitRequest>({
    query: (request) => ({
      url: '/oms/submit/TakeProfit',
      method: 'POST',
      body: generateBody(request),
      timeout: 10000,
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformResponse: (response: string) => {
      return {
        type: 'success',
        orderId: response,
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | { validationResponseDetailContent: ValidationResponseDetailContent[] };
      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
                response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );

        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }

        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (request: SubmitTakeProfitRequest): SubmitTakeProfitBody => {
  // Here you can add any additional processing of the request body if needed
  return {
    isGtc: request.expiry.type === 'GoodTillCancelled',
    bdrId: String(request.clientId),
    amountInCcy1: request.notionalCurrencyIndex === 1 ? request.notional : undefined,
    amountInCcy2: request.notionalCurrencyIndex === 2 ? request.notional : undefined,
    ccyPair: `${request.currency1}/${request.currency2}`,
    expiryDay: request.expiry.type === 'ExpiryDate' ? request.expiry.date : undefined,
    expiryTime: request.expiry.type === 'ExpiryDate' ? request.expiry.time : undefined,
    way: request.way,
    limitPrice: isDefined(request.limitPrice) ? String(request.limitPrice) : '',
    base64Emails: isDefined(request.notificationEmails) ? toBase64(request.notificationEmails.join(';')) : '', // base64 encode the emails
    margin: request.margin ?? 0,
    customerPrice: isDefined(request.customerPrice) ? String(request.customerPrice) : '',
    localTimeWithOffSet: request.localTimeWithOffSet,
  };
};

function isValidateOrderResponse(
  response: unknown,
): response is { validationResponseDetailContent: ValidationResponseDetailContent[]; isReadyToSubmit: boolean } {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToSubmit' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
